import httpClient from '@/utils/httpClient';

export const getAgentStatus = () => httpClient.get('/api/user-agents/dashboard/status');
export const getAgentUsage = (params) => httpClient.get('/api/user-agents/dashboard/usage', { params });
export const getAgentAudit = (params) => httpClient.get('/api/user-agents/dashboard/audit', { params });
export const getAgentTasks = (params) => httpClient.get('/api/user-agents/dashboard/tasks', { params });
export const getAgentTaskRuns = (params) => httpClient.get('/api/user-agents/dashboard/task-runs', { params });
export const getAgentDigest = (params) => httpClient.get('/api/user-agents/dashboard/digest', { params });
export const getAgentLogs = (params) => httpClient.get('/api/user-agents/dashboard/logs', { params });
// Unified per-run history (every run + trigger) for one agent group — backs the Runs & History tab.
export const getAgentRuns = (params) => httpClient.get('/api/user-agents/dashboard/runs', { params });

export const pauseTask = (taskId) => httpClient.post(`/api/user-agents/dashboard/tasks/${taskId}/pause`);
export const resumeTask = (taskId) => httpClient.post(`/api/user-agents/dashboard/tasks/${taskId}/resume`);
export const invokeAgent = (data) => httpClient.post('/api/user-agents/dashboard/invoke', data);

// VM host resource usage (CPU/RAM/disk) — 502 until the VM build exposes /api/agents/vm-stats.
export const getVmStats = () => httpClient.get('/api/user-agents/dashboard/vm-stats');
// VM reachability + uptime from Core's SatelliteHealthService, plus the caller's active-agent count.
export const getVmHealth = () => httpClient.get('/api/user-agents/vm-health');
// Team + per-agent spend in USD. Core converts VM tokens at each agent's model rate — do not
// re-derive $ on the client; the model rates live in AgentCostHelper.
// since = 'today' (default) | 'week'
export const getCostSummary = (since = 'today') => httpClient.get('/api/user-agents/dashboard/cost-summary', { params: { since } });
// Cancel a currently-executing run (distinct from pausing a scheduled task's future runs).
export const cancelRun = (runId, group) => httpClient.post(`/api/user-agents/dashboard/runs/${runId}/cancel`, null, { params: { group } });

// ===== Team endpoints (hire/retire/invoke per-profile agents) =====
// Core serializes every Web API response in camelCase (Global.asax.cs), while these screens read the
// rows as PascalCase. Normalize the /team rows once here — read raw, and every `row.Status` /
// `row.GroupName` / `row.Id` across the Agents screens silently reads undefined.
const toPascalRow = (row) =>
  row && typeof row === 'object'
    ? Object.keys(row).reduce((out, k) => {
        out[k.charAt(0).toUpperCase() + k.slice(1)] = row[k];
        return out;
      }, {})
    : row;

export const getTeam = () => httpClient.get('/api/user-agents/team').then((res) => ({ ...res, data: Array.isArray(res?.data) ? res.data.map(toPascalRow) : res?.data }));
export const hireAgent = (data) => httpClient.post('/api/user-agents/team/hire', data);
export const retireAgent = (userAgentId) => httpClient.delete(`/api/user-agents/team/${userAgentId}`);
export const updateAgentSettings = (userAgentId, data) => httpClient.patch(`/api/user-agents/team/${userAgentId}`, data);
export const invokeTeamAgent = (userAgentId, prompt) => httpClient.post(`/api/user-agents/team/${userAgentId}/invoke`, { Prompt: prompt });
export const getTeamMessages = (userAgentId, limit) => httpClient.get(`/api/user-agents/team/${userAgentId}/messages`, { params: { limit } });

// Emergency stop / undo. Core flips every agent's Status, which gates invoke and delegation.
export const pauseAllAgents = () => httpClient.post('/api/user-agents/team/pause-all');
export const resumeAllAgents = () => httpClient.post('/api/user-agents/team/resume-all');

// ===== Proactive-message unread badge =====
export const getUnreadCounts = () => httpClient.get('/api/user-agents/team/unread-counts');
export const markAgentRead = (userAgentId) => httpClient.post(`/api/user-agents/team/${userAgentId}/mark-read`);

// ===== Agent memory (editable durable facts; Core is source of truth) =====
export const getAgentMemory = (userAgentId) => httpClient.get(`/api/user-agents/team/${userAgentId}/memory`);
export const putAgentMemory = (userAgentId, data) => httpClient.put(`/api/user-agents/team/${userAgentId}/memory`, data);
export const deleteAgentMemory = (userAgentId, kind) => httpClient.delete(`/api/user-agents/team/${userAgentId}/memory/${kind}`);

// Read-only: facts the agent auto-learned from conversations (supermemory on the VM)
export const getAgentLearnedMemory = (userAgentId) => httpClient.get(`/api/user-agents/team/${userAgentId}/learned-memory`);

// Per-agent risk policy (safe/reversible/irreversible → auto|approve). Reuses the AI-Assistant risk taxonomy.
export const getAgentPolicy = (userAgentId) => httpClient.get(`/api/user-agents/team/${userAgentId}/policy`);
export const putAgentPolicy = (userAgentId, body) => httpClient.put(`/api/user-agents/team/${userAgentId}/policy`, body);

// ===== Telegram bot setup (manager only) =====
// Validate token via Telegram getMe — returns { Ok, BotUsername, BotId, FirstName, Error }
export const validateTelegramBot = (token) => httpClient.post('/api/user-agents/team/telegram/validate', { Token: token });
// Detect chat_id by polling getUpdates — returns { Ok, ChatId, Error } when user sends /start
export const detectTelegramChatId = (token) => httpClient.post('/api/user-agents/team/telegram/detect-chat', { Token: token });
// Disconnect bot from a manager
export const disconnectTelegramBot = (userAgentId) => httpClient.delete(`/api/user-agents/team/${userAgentId}/telegram`);

// ===== Approvals (human-in-the-loop) =====
// state = 'pending' (default) | 'all'
export const getApprovals = (state = 'pending') => httpClient.get('/api/user-agents/team/approvals', { params: { state } });
// data = { Decision: 'approved'|'rejected'|'edited', Reason?, EditedPreviewJson? }
export const decideApproval = (id, data) => httpClient.post(`/api/user-agents/team/approvals/${id}/decide`, data);

export const getIssues = (params) => httpClient.get('/api/user-agents/issues', { params });
export const getIssue = (id) => httpClient.get(`/api/user-agents/issues/${id}`);
export const createIssue = (data) => httpClient.post('/api/user-agents/issues', data);
export const updateIssue = (id, data) => httpClient.put(`/api/user-agents/issues/${id}`, data);
export const resolveIssue = (id, data) => httpClient.put(`/api/user-agents/issues/${id}/resolve`, data);
export const deleteIssue = (id) => httpClient.delete(`/api/user-agents/issues/${id}`);

export const getGoals = (params) => httpClient.get('/api/user-agents/goals', { params });
export const createGoal = (data) => httpClient.post('/api/user-agents/goals', data);
export const updateGoal = (id, data) => httpClient.put(`/api/user-agents/goals/${id}`, data);
export const updateGoalProgress = (id, data) => httpClient.put(`/api/user-agents/goals/${id}/progress`, data);
export const deleteGoal = (id) => httpClient.delete(`/api/user-agents/goals/${id}`);

export const getDocuments = (params) => httpClient.get('/api/user-agents/documents', { params });
export const getDocument = (id) => httpClient.get(`/api/user-agents/documents/${id}`);
export const createDocument = (data) => httpClient.post('/api/user-agents/documents', data);
export const updateDocument = (id, data) => httpClient.put(`/api/user-agents/documents/${id}`, data);
export const deleteDocument = (id) => httpClient.delete(`/api/user-agents/documents/${id}`);
